import { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { Badge, Card, Button } from 'antd';
import { EnvironmentOutlined } from '@ant-design/icons';
import CountryService from '../../../domain/Country/CountryService';
import LocationsContext from '@/pages/Locations/context';
import { CountryOrganization, Country } from '@/domain/Country/Country';
import './index.scss';
import { DataGrid } from '@hydrodigital/design-system';
import type { ComboboxOption } from '@hydrodigital/combobox';
import CountrySearchCombobox from './CountrySearchCombobox';

const countryService = new CountryService();

type Props = {
	displayMode: string;
};

const CountriesByOrganization = ({ displayMode }: Props) => {

	const navigate = useNavigate();
	const locationsContext = useContext(LocationsContext);
	const [countries, setCountries] = useState<Country[]>([]);

	const [searchValue, setSearchValue] = useState('');

	const countryOptions: ComboboxOption[] = countries.map((c) => ({
		value: String(c.id),
		label: c.name,
	}));

	function handleCountrySelect(value: string) {
		const selected = countries.find((c) => String(c.id) === value);
		if (selected) {
			toDetails(String(selected.id), selected.name);
		}
	}

	async function getCountries() {
		const res = await countryService.getCountries();
		setCountries(res);
	}

	function toDetails(id: string, name: string) {
		navigate({
			pathname: `/locations/${id}`,
			search: `?name=${name}`,
		});
	}

	useEffect(() => {
		getCountries();
	}, []);

	const gridColumns = [
		{
			header: 'Region name',
			accessorKey: 'name',
			cell: ({ cell }: any) => <span className="table-data-gis">{cell.getValue()}</span>,
		},
		{
			header: 'Locations',
			accessorKey: 'locationsQuantity',
			cell: ({ cell }: any) => <span className="table-data-gis">{cell.getValue()}</span>,
		},
		{
			header: 'Sub-locations',
			accessorKey: 'subLocationsQuantity',
			cell: ({ cell }: any) => <span className="table-data-gis">{cell.getValue()}</span>,
		},
		{
			header: 'Status',
			accessorKey: 'status',
			cell: ({ row }: any) => (
				<div className="flex column">
					<div className="flex">
						<Badge status="success" style={{ marginRight: '5px' }} />
						{row.original.locationsActive} active
					</div>
					<div>
						<Badge status="default" style={{ marginRight: '5px' }} />
						{row.original.locationsInactive} inactive
					</div>
				</div>
			),
		},
		{
			header: '',
			accessorKey: 'id',
			cell: ({ row }: any) => (
				<span
					style={{
						color: '#22A2CA',
						textDecorationLine: 'underline',
						cursor: 'pointer',
					}}
					onClick={() => toDetails(row.original.id, row.original.name)}
				>
					View Locations
				</span>
			),
		},
	];

	return locationsContext.countriesByOrganization.length > 0 ? (
		<div className="w-full flex column">
			<div style={{ width: '250px', marginBottom: '2rem' }}>
				<CountrySearchCombobox options={countryOptions} value={searchValue} onSelect={handleCountrySelect} />
			</div>
			{displayMode === 'table' ? (
				<div className="w-full">
					<DataGrid
						data={locationsContext.countriesByOrganization}
						columns={gridColumns}
						pagination={{
							enabled: false,
							defaultPageSize: 10,
						}}
					/>
				</div>
			) : (
				<div className="w-full card-display__container">
					{locationsContext.countriesByOrganization.map((item) => (
						<Card
							key={item.id}
							cover={
								<div
									className="flex items-center"
									style={{
										padding: '1.5rem',
										borderBottom: '1px solid #C4CCD5',
									}}
								>
									<EnvironmentOutlined
										style={{
											marginRight: '10px',
											fontSize: '1.5rem',
										}}
										rev={undefined}
									/>
									<span style={{ marginRight: '10px' }}>{item.name}</span>
									<Button
										type="primary"
										shape="round"
										ghost
										style={{ marginLeft: 'auto' }}
										onClick={() => toDetails(item.id, item.name)}
									>
										View locations
									</Button>
								</div>
							}
							style={{ maxWidth: '400px' }}
						>
							<div className="card-content__container">
								<div>Locations</div>
								<div>{item.locationsQuantity}</div>
								<div>
									<Badge status="success" style={{ marginRight: '5px' }} /> Active
								</div>
								<div>{item.locationsActive}</div>
								<div>Sub-locations</div>
								<div>{item.subLocationsQuantity}</div>
								<div>
									<Badge status="default" style={{ marginRight: '5px' }} /> Inactive
								</div>
								<div>{item.locationsInactive}</div>
							</div>
						</Card>
					))}
				</div>
			)}
		</div>
	) : (
		<div className="flex flex-column self-center text-center" style={{ maxWidth: '300px' }}>
			<span className="text-title-page" style={{ marginBottom: '10px' }}>
				No Regions - yet!
			</span>
			<span className="text-subtitle-page" style={{ marginBottom: '10px' }}>
				Looks that you haven’t select any Region, no worries. Search to start
			</span>
			<div className="self-center" style={{ width: '250px' }}>
				<CountrySearchCombobox options={countryOptions} value={searchValue} onSelect={handleCountrySelect} />
			</div>
		</div>
	);
};

export default CountriesByOrganization;
